import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { type BankOffer, useGetClientBankOffersQuery } from 'services/api/clients.api';
import priceFormat from 'services/format/priceFormat';
import {
  META_BLUE,
  NAVY,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';

const STATUS_TONE: Record<string, string> = {
  created: NAVY,
  sent_to_bank: META_BLUE,
  pre_approved: STATUS_GOOD,
  rejected_by_bank: STATUS_WARN,
};

const StatusBadge: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const tone = STATUS_TONE[status] ?? NAVY;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.25,
        borderRadius: RADIUS_COIN,
        bgcolor: alpha(tone, 0.12),
        color: tone,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.7,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
    {label}:{' '}
    <Box component="span" sx={{ color: NAVY, fontWeight: 600 }}>
      {value}
    </Box>
  </Typography>
);

const BankOfferCard: React.FC<{ offer: BankOffer }> = ({ offer }) => {
  const [expanded, setExpanded] = useState(false);
  const hasResponse = !!offer.rawBankResponse;

  return (
    <Box sx={{ borderRadius: RADIUS_ITEM, bgcolor: SURFACE_CONTAINER_LOWEST, boxShadow: SHADOW, overflow: 'hidden' }}>
      <Box
        onClick={hasResponse ? () => setExpanded((prev) => !prev) : undefined}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, cursor: hasResponse ? 'pointer' : 'default' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {offer.bank}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatusBadge status={offer.status} label={offer.mappedStatus} />
            {hasResponse && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Metric label="Monto máx." value={offer.maxAmount != null ? priceFormat.format(offer.maxAmount) : '-'} />
          <Metric label="Plazo máx." value={offer.maxTerm != null ? `${offer.maxTerm} meses` : '-'} />
        </Box>
      </Box>

      {hasResponse && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
              Respuesta del banco
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 0.5,
                p: 1.5,
                borderRadius: RADIUS_ITEM,
                bgcolor: alpha(NAVY, 0.04),
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 240,
              }}
            >
              {JSON.stringify(offer.rawBankResponse, null, 2)}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const BankOffersSection: React.FC<{ clientId: number }> = ({ clientId }) => {
  const { data, isLoading, error } = useGetClientBankOffersQuery(clientId.toString());

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Error al cargar las ofertas de banco. Por favor, intenta nuevamente.
        </Typography>
      </Box>
    );
  }

  const bankOffers = data?.bankOffers ?? [];

  if (bankOffers.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Sin ofertas de banco registradas.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {bankOffers.map((offer) => (
        <BankOfferCard key={offer.bank} offer={offer} />
      ))}
    </Box>
  );
};

export default BankOffersSection;
